'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { API_BASE_URL } from '@/lib/config';

interface PublicSessionContextType {
  isPublicSession: boolean;
  isCheckingSession: boolean;
  sessionId: string | null;
}

const PublicSessionContext = createContext<PublicSessionContextType>({
  isPublicSession: false,
  isCheckingSession: false,
  sessionId: null,
});

export const usePublicSession = () => useContext(PublicSessionContext);

export function PublicSessionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const [isPublicSession, setIsPublicSession] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  useEffect(() => {
    // Extract session ID from URL patterns like /chat/[id], /strategy/[id]
    const chatMatch = pathname?.match(/^\/(chat|strategy)\/([^/]+)$/);
    
    if (chatMatch) {
      const extractedSessionId = chatMatch[2];
      setSessionId(extractedSessionId);
      setIsCheckingSession(true);

      // Call backend API directly to check session visibility
      fetch(`${API_BASE_URL}/sessions/${extractedSessionId}`, {
        cache: 'no-store',
        credentials: 'include',
      })
        .then((res) => {
          if (!res.ok) {
            setIsPublicSession(false);
            return null;
          }
          return res.json();
        })
        .then((sessionData) => {
          if (sessionData) {
            setIsPublicSession(sessionData.visibility === 'public');
          }
        })
        .catch((error) => {
          setIsPublicSession(false);
        })
        .finally(() => {
          setIsCheckingSession(false);
        });
    } else {
      // Not a session page, reset state
      setSessionId(null);
      setIsPublicSession(false);
      setIsCheckingSession(false);
    }
  }, [pathname]);

  return (
    <PublicSessionContext.Provider
      value={{ isPublicSession, isCheckingSession, sessionId }}
    >
      {children}
    </PublicSessionContext.Provider>
  );
}
